import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  AppProvider, Card, Page, Frame, DisplayText, Tabs, Toast, Banner, Icon 
} from '@shopify/polaris';
import { ViewMajorMonotone } from '@shopify/polaris-icons';
import translations from '@shopify/polaris/locales/es.json';

import ResourceTableHeaders from '@presentational/ResourceTableHeaders';
import ResourceTableBody from '@presentational/ResourceTableBody';

import FormPageActions from 'components/container/FormPageActions';

import { transferShape } from '@utils/shapeProps';
import axios from '@utils/axios';
import { transferItemColumns } from '@utils/recordHelpers';

const propTypes = {
  transfer: transferShape.isRequired,
  csvURL: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
  isSeller: PropTypes.bool,
  manualSyncItemsURL: PropTypes.string,
  cancelURL: PropTypes.string,

  variantLogsURLBase: PropTypes.string.isRequired,
  legacyInformationURL: PropTypes.string,
};
const defaultProps = {

}

const TransferShow = (props) => {
  const [loading, setLoading] = useState(false);
  const [tabSelected, setTabSelected] = useState(0);
  const [manualSyncToast, setManualSyncToast] = useState({
    title: '',
    errors: [],
    message: '',
  });

  const transfer = props.transfer;
  const brightpearlPoURL = props.isAdmin && !!transfer.bright_pearl_po_id && `https://euw1.brightpearlapp.com/patt-op.php?scode=invoice&oID=${transfer.bright_pearl_po_id}` // Only production URL is set

  const tabs = [
    { id: 'information', content: I18n.t("enum.information"), accessibilityLabel: 'Admin', },
    { id: 'logs', content: I18n.t("enum.logs"), accessibilityLabel: 'Admin', },
    { id: 'sales', content: "Detalle de Ventas", accessibilityLabel: 'sales', },
  ];

  const handleTabChange = useCallback( (selectedTabIndex) => setTabSelected(selectedTabIndex),  [], );

  const handleManualItemsSync = async () => {
    setLoading(true);
    try {
      const response = await axios.post(props.manualSyncItemsURL);
      const { data } = response;
      setManualSyncToast({
        title: data.message,
        message: data.message,
        errors: data.errors,
      });
    } catch (error) {
      console.log(error)
      setManualSyncToast((state) => ({
        ...state,
        message: "Ocurrio un error de conexión." 
      }))
    }
    setLoading(false);
  };

  const closeToast = () => setState( state => ({ ...state, message: null }) );

  // Displayed directly 
  let secondaryActions = [];
  // Purchase Order URL
  if(props.isAdmin && brightpearlPoURL){
    secondaryActions = [{
        content: 'Ver PO en Brightpearl',
        external: true,
        url: brightpearlPoURL,
        icon: ViewMajorMonotone,
      },
      ...secondaryActions
    ]
  }

  secondaryActions = secondaryActions.length > 0 ? secondaryActions : undefined; // empty array raises an error
  // end secondaryActions

  // actionGroupActions (grouped in a dropdown)
  let actionGroupActions= [{
      content: 'Exportar CSV',
      accessibilityLabel: 'Exportar CSV',
      url: props.csvURL,
      external: true,
    }
  ];
  // Manual sync
  if(props.manualSyncItemsURL){
    actionGroupActions = [
      ...actionGroupActions,
      {
        content: "Sincronizar productos en BP",
        onAction: handleManualItemsSync,
        disabled: loading,
      },
    ]
  };
  if(props.legacyInformationURL){
    actionGroupActions = [
      ...actionGroupActions,
      {
        content: "Información Legacy",
        url: props.legacyInformationURL,
        external: true,
      },
    ]
  }

  // Primary Action (Edit)
  let primaryAction = undefined;
  if(props.editURL){
    primaryAction = {
      content: I18n.t("actions.edit"),
      url: props.editURL,
      primary: false,
    }
  }

  // end actionGroupActions

  // Because onSelection comes from PolarisWrapper it cannot be accesed directly on Container.
  return (
    <AppProvider i18n={translations}>
      <Frame>
        <Page
          title= {I18n.t("activerecord.models.transfer.one") + ` ${transfer.name}`}
          breadcrumbs= {[{content: I18n.t("actions.back"), url: '/transfers'}]}
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          actionGroups={[{
            title: "Más Acciones",
            actions: actionGroupActions,
          }]}
          fullWidth
        >
          <Card sectioned>
            <div className="row">
              <div className='col-12 col-md-5'>
                <h2>
                  <strong>{ transfer.vendor }</strong>
                </h2>
                <HeadingItem 
                  title={ I18n.t("activerecord.attributes.transfer.invoice_number") }
                  content={ transfer.invoice_number }
                />
              </div>

              <div className='col-12 col-md-6'>
                <HeadingItem
                  title={ I18n.t("activerecord.attributes.general.created_at") }
                  content={ I18n.l("date.formats.unimart", transfer.created_at) }
                />
                <HeadingItem
                  title={ I18n.t("activerecord.attributes.transfer.invoice_date") }
                  content={ transfer.invoice_date && I18n.l("date.formats.unimart", transfer.invoice_date) }
                />
              </div>

              <div className='col-12 col-md-1'>
                <h4>
                  Recibido
                </h4>
                <DisplayText size="medium">
                  <strong>
                    <ShipmentCounter quantity={transfer.total_to_ship} completed={ transfer.total_shipped } />
                  </strong>
                </DisplayText>
              </div>
            </div>
          </Card>

          { manualSyncToast.errors > 0 &&
            <div className="mt10">
              <Banner
                title={ manualSyncToast.title }
                status="critical"
              >
                { manualSyncToast.errors.map(( error, index ) =>
                  <p key={index}>{error}</p>
                )}
              </Banner>
            </div>
          }

          <Card>
            <Banner title="Códigos de barra" status="info">
              <p>Asegurese los códigos de barras que salen a continuación coincidan con los del producto físico.</p>
            </Banner>
            <Card.Section>
              <div className='mt20'>
                <ResourceTableHeaders columnsInfo={ transferItemColumns } />

                <ResourceTableBody
                  columnsInfo={ transferItemColumns }
                  items={ transfer.products_transfers }
                />
                <ResourceTableBody 
                  items={[{ total: transfer.total_price_money }]} 
                  columnsInfo={[
                    { size: 8, header: "", accessor: "offset" },
                    { size: 1, header: "", accessor: () => <strong>Total</strong> },
                    { size: 2, header: "", accessor: ({ total }) => <strong>{ total }</strong> }
                  ]}
                />
              </div>
            </Card.Section>
          </Card>
          
          { !!manualSyncToast.message &&
            <Toast
              content={ manualSyncToast.message }
              onDismiss={ closeToast } />
          }

          <FormPageActions
            loading={ loading }
            deleteURL={ props.cancelURL }
            deleteText="Cancelar"
            httpMethod="PUT"
          />
        </Page>
      </Frame>
    </AppProvider>
  )
};

const HeadingItem = ({ title, content }) => {
  return (
    <h4><strong>{ title }:</strong> { content }</h4>
  )
}

// Small component to display the quantity of items and the completed ones
const ShipmentCounter = ({ quantity, completed }) => (
  <span>
    { `${completed}/${quantity}` }
  </span>
)



TransferShow.propTypes = propTypes;
TransferShow.defaultProps = defaultProps;

export default TransferShow;
